import { useForm } from "react-hook-form";
import { useEffect, useState, useCallback } from "react";

import { useCreateSimulation } from "hooks/api/use-simulations";

import { useSmsSimulatorContext } from "containers/sms-simulator/sms-simulator.context";

import { SmsSimulatorFormField } from "containers/sms-simulator/sms-simulator.types";
import {
  SMS_SIMULATOR_FORM_VALIDATION_SCHEMA,
  SmsSimulatorFormData,
} from "containers/sms-simulator/sms-simulator-simulator/sms-simulator-simulations-form.constants";

export const useSmsSimulatorForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const {
    setCurrentSimulation,
    currentSimulation,
    clients,
    simulatorFormDraft,
    setSimulatorFormDraft,
  } = useSmsSimulatorContext();

  const createSimulation = useCreateSimulation();

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isValid },
    reset,
  } = useForm<SmsSimulatorFormData>({
    resolver: SMS_SIMULATOR_FORM_VALIDATION_SCHEMA,
    mode: "onChange",
    defaultValues: {
      client: undefined,
      testSmsText: "",
      systemPrompt:
        "You are an enrollment assistant replying to a student via SMS. Your tone should be friendly, brief, and informative. Avoid sounding robotic or overly formal.",
    },
  });

  useEffect(() => {
    if (!clients.length) return;
    if (simulatorFormDraft.clientId) {
      const draftClient = clients.find((client) => client.value === simulatorFormDraft.clientId);
      if (draftClient) {
        setValue("client", draftClient);
      } else {
        setValue("client", clients[0]);
      }
    } else {
      setValue("client", clients[0]);
    }
  }, [clients, setValue, simulatorFormDraft.clientId]);

  useEffect(() => {
    if (simulatorFormDraft.testSmsText) {
      setValue("testSmsText", simulatorFormDraft.testSmsText);
    }

    if (simulatorFormDraft.systemPrompt) {
      setValue("systemPrompt", simulatorFormDraft.systemPrompt);
    }
  }, [setValue, simulatorFormDraft.testSmsText, simulatorFormDraft.systemPrompt]);

  useEffect(() => {
    if (currentSimulation) {
      const matchingClient = clients.find((option) => option.value === currentSimulation.clientId);

      if (matchingClient) {
        setValue("client", matchingClient);
      }
      setValue("testSmsText", currentSimulation.testSmsText);
      setValue("systemPrompt", currentSimulation.systemPrompt);

      trigger();
    }
  }, [currentSimulation, clients, setValue, trigger, setSimulatorFormDraft]);

  const updateDraft = useCallback(
    (field: SmsSimulatorFormField, value: any) => {
      const draftKey = field === "client" ? "clientId" : field;
      const draftValue = field === "client" ? value?.value : value;

      setSimulatorFormDraft((prev) => ({
        ...prev,
        [draftKey]: draftValue,
      }));
    },
    [setSimulatorFormDraft],
  );

  const onSubmit = useCallback(
    async (data: SmsSimulatorFormData) => {
      try {
        setIsLoading(true);

        setIsTransitioning(true);

        const newSimulation = await createSimulation.mutateAsync({
          clientId: data.client.value,
          systemPrompt: data.systemPrompt,
          testSmsText: data.testSmsText,
        });

        setCurrentSimulation(newSimulation);

        requestAnimationFrame(() => {
          setIsTransitioning(false);
        });
      } catch (error) {
        console.error("Failed to create simulation:", error);
        setIsTransitioning(false);
      } finally {
        setIsLoading(false);
      }
    },
    [createSimulation, setCurrentSimulation],
  );

  const handleRefresh = useCallback(() => {
    setCurrentSimulation(null);
    setSimulatorFormDraft({});
    reset();

    if (clients.length > 0) {
      setValue("client", clients[0]);
    }
  }, [setCurrentSimulation, setSimulatorFormDraft, reset, clients, setValue]);

  return {
    control,
    handleSubmit,
    errors,
    isValid,
    isLoading,
    isTransitioning,
    onSubmit,
    updateDraft,
    handleRefresh,
  };
};
